import { AfterViewInit, Component, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { CompositeFilterDescriptor, DataSourceRequestState, FilterDescriptor } from '@progress/kendo-data-query';
import { DialogService } from '@progress/kendo-angular-dialog';
import { AppService } from '../../app.service';
import { ListPageBase } from '../../shared/components/list-page-base/list-page-base';
import { ExportExcelFromGridService } from '../../shared/services/export-excel-from-grid.service';
import { GridSettings } from '../../shared/models/grid/grid-settings';
import { LeadListItem } from '../../shared/models/lead/lead-list-item';
import { getLeadDialogTitle, applyLeadDialogTitle } from '../lead-dialog-title';
import { LeadsService } from '../../shared/services/leads.service';
import { StatePersistingService } from '../../shared/services/state-persisting.service';
import { LeadDetailDialogComponent } from '../lead-detail-dialog/lead-detail-dialog.component';

@Component({
	selector: 'leads-list',
	templateUrl: './list.page.html',
	styleUrls: ['./list.page.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class LeadsListPage extends ListPageBase<LeadListItem> implements AfterViewInit {
	public statusNotProcessedId = 1;
	public initialized = true;
	public enableSaveGridSettings = true;

	gridSettingsStorageKey = 'leads-grid-settings-v4';
	gridSettingsFilterStorageKey = 'leads-grid-settings-filter-v4';

	public gridSettingsFilter: GridSettings = {
		state: {
			skip: 0,
			take: 25,
			sort: [{ field: 'createdDate', dir: 'desc' }],
			filter: { logic: 'and', filters: [] }
		},
		columnsConfig: [
			{ orderIndex: 0, field: 'createdDate', title: 'Дата', filter: 'date', format: 'dd.MM.yyyy HH:mm', filterable: true, width: 350, hidden: false },
			{ orderIndex: 1, field: 'statusName', title: 'Статус', filter: 'lookup', filterable: true, width: 300, hidden: false, filterField: 'statusId' },
			{ orderIndex: 2, field: 'serviceCenterName', title: 'Сервисный центр', filter: 'lookup', filterable: true, width: 220, hidden: false, filterField: 'serviceCenterId' },
			{ orderIndex: 3, field: 'customerName', title: 'Имя, отчество', filter: 'string', filterable: true, width: 220, hidden: false },
			{ orderIndex: 4, field: 'description', title: 'Описание', filter: 'string', filterable: true, width: 320, hidden: false },
			{ orderIndex: 5, field: 'customerPhone', title: 'Телефон', filter: 'string', filterable: true, width: 170, hidden: false },
			{ orderIndex: 6, field: 'customerAddress', title: 'Адрес', filter: 'string', filterable: true, width: 280, hidden: false },
			{ orderIndex: 7, field: 'customerEmail', title: 'Email', filter: 'string', filterable: true, width: 220, hidden: false },
			{ orderIndex: 8, field: 'formName', title: 'Форма', filter: 'string', filterable: true, width: 220, hidden: false },
			{ orderIndex: 9, field: 'initTypeName', title: 'Источник', filter: 'lookup', filterable: true, width: 200, hidden: false, filterField: 'initTypeId' },
			{ orderIndex: 10, field: 'lastComment', title: 'Последний комментарий', filter: 'string', filterable: true, width: 320, hidden: false },
		]
	};

	public gridSettings: GridSettings = {
		state: {
			skip: 0,
			take: 25,
			sort: [{ field: 'createdDate', dir: 'desc' }],
			// Форма как у multiselect-lookup-filter-virtual (вложенный composite), иначе тег статуса в шапке не показывается
			filter: {
				logic: 'and',
				filters: [
					{
						logic: 'or',
						filters: [{ field: 'statusId', operator: 'eq', value: this.statusNotProcessedId }],
					},
				],
			} as CompositeFilterDescriptor,
		},
		columnsConfig: [
			{ orderIndex: 0, field: 'createdDate', title: 'Дата', filter: 'date', format: 'dd.MM.yyyy HH:mm', filterable: false, width: 350, hidden: false },
			{ orderIndex: 1, field: 'statusName', title: 'Статус', filter: 'lookup', filterable: false, width: 300, hidden: false, filterField: 'statusId' },
			{ orderIndex: 2, field: 'serviceCenterName', title: 'Сервисный центр', filter: 'lookup', filterable: false, width: 220, hidden: false, filterField: 'serviceCenterId' },
			{ orderIndex: 3, field: 'customerName', title: 'Имя, отчество', filter: 'string', filterable: false, width: 220, hidden: false },
			{ orderIndex: 4, field: 'description', title: 'Описание', filter: 'string', filterable: false, width: 320, hidden: false },
			{ orderIndex: 5, field: 'customerPhone', title: 'Телефон', filter: 'string', filterable: false, width: 170, hidden: false },
			{ orderIndex: 6, field: 'customerAddress', title: 'Адрес', filter: 'string', filterable: false, width: 280, hidden: false },
			{ orderIndex: 7, field: 'customerEmail', title: 'Email', filter: 'string', filterable: false, width: 220, hidden: false },
			{ orderIndex: 8, field: 'formName', title: 'Форма', filter: 'string', filterable: false, width: 220, hidden: false },
			{ orderIndex: 9, field: 'initTypeName', title: 'Источник', filter: 'lookup', filterable: false, width: 200, hidden: false, filterField: 'initTypeId' },
			{ orderIndex: 10, field: 'lastComment', title: 'Последний комментарий', filter: 'string', filterable: false, width: 320, hidden: false },
		]
	};

	constructor(
		protected appService: AppService,
		protected dataService: LeadsService,
		protected router: Router,
		protected persistingService: StatePersistingService,
		protected exportExcelFromGridService: ExportExcelFromGridService,
		private dialogService: DialogService
	) {
		super(router, appService, dataService, persistingService, exportExcelFromGridService);
		this.appService.pageTitle = 'Входящие лиды';
		this.filtersBlockCollapsedStorageKey = 'leadsListFiltersBlockCollapsed';
		this.restoreFiltersBlockCollapsedState();
	}

	ngAfterViewInit(): void {
		this.syncMainGridHeight();
	}

	onListPageInit(): void {
		this.normalizeCreatedDateFilter(this.gridSettings.state.filter as CompositeFilterDescriptor);
		this.ensureStatusFilterShapeForMultiselect(this.gridSettings.state.filter as CompositeFilterDescriptor);
	}

	processListItems(_: LeadListItem[]): void {
	}

	/** Описание в таблице = заголовок формы лида (formName + тип заявки/специфика из rawExtraData). */
	public getLeadDescription(lead: LeadListItem): string {
		return getLeadDialogTitle({
			formName: lead.formName,
			description: lead.description,
			rawExtraData: lead.rawExtraData,
		});
	}

	/** Стили бейджа статуса: поддерживаются #rrggbb и имена CSS (red, teal, …). */
	public leadStatusBadgeStyle(item: LeadListItem): { [key: string]: string } | null {
		const bg = item.statusBlockColor != null ? String(item.statusBlockColor).trim() : '';
		const fg = item.statusTextColor != null ? String(item.statusTextColor).trim() : '';
		if (!bg && !fg) {
			return null;
		}
		const s: { [key: string]: string } = {};
		if (bg) {
			s['background-color'] = bg;
		}
		if (fg) {
			s['color'] = fg;
		}
		return s;
	}

	public clearFilters(): void {
		this.gridSettings.state = {
			...this.gridSettings.state,
			skip: 0,
			filter: {
				logic: 'and',
				filters: [
					{
						logic: 'or',
						filters: [{ field: 'statusId', operator: 'eq', value: this.statusNotProcessedId }],
					},
				],
			} as CompositeFilterDescriptor,
		};
		this.gridSettingsFilter.state = {
			skip: 0,
			take: this.gridSettings.state.take,
			sort: [...this.gridSettings.state.sort],
			filter: JSON.parse(JSON.stringify(this.gridSettings.state.filter)) as CompositeFilterDescriptor
		};
		if (this.enableSaveGridSettings) {
			this.saveGridSettings();
			this.saveGridSettings(true);
		}
		this.appService.clearAllGridFilters.next();
		this.onGridStateChange();
		this.loadData();
	}

	public openLead(lead: LeadListItem): void {
		const w = this.appService.isMobileDevice ? '95%' : '980px';
		const titleSource = {
			formName: lead.formName,
			description: lead.description,
			rawExtraData: lead.rawExtraData,
		};
		const title = getLeadDialogTitle(titleSource);
		const dialogRef = this.dialogService.open({
			content: LeadDetailDialogComponent,
			title,
			width: w,
			height: '85%',
		});
		applyLeadDialogTitle(dialogRef, titleSource);
		const inst = dialogRef.content.instance as LeadDetailDialogComponent;
		inst.refreshList = () => this.loadData();
		inst.leadId = lead.leadId;
	}

	public loadData(state: DataSourceRequestState = this.gridSettings.state): void {
		this.loading = true;
		const visibleColumnNames = this.gridSettings.columnsConfig.filter(x => !x.hidden).map(x => x.field);

		this.setGridSettingsState(this.gridSettings);
		if (this.gridSettings.state.filter) {
			this.normalizeCreatedDateFilter(this.gridSettings.state.filter as CompositeFilterDescriptor);
		}

		this.dataService.leadList(state, 
			visibleColumnNames
		).subscribe(data => {
			this.listViewModel = data;
			this.loading = false;
		}, () => {
			this.loading = false;
		});
	}

	/** Плоский descriptor statusId из старых сохранённых настроек → форма, которую понимает multiselect-lookup-filter-virtual. */
	private ensureStatusFilterShapeForMultiselect(filter: CompositeFilterDescriptor): void {
		if (!filter || !filter.filters || !filter.filters.length) {
			return;
		}
		const i = filter.filters.findIndex(
			f => 'field' in f && (f as FilterDescriptor).field === 'statusId',
		);
		if (i < 0) {
			return;
		}
		const fd = filter.filters[i] as FilterDescriptor;
		filter.filters[i] = {
			logic: 'or',
			filters: [{ ...fd }],
		} as CompositeFilterDescriptor;
	}

	private normalizeCreatedDateFilter(filter: CompositeFilterDescriptor): void {
		if (!filter || !filter.filters) {
			return;
		}

		filter.filters.forEach(x => {
			const nested = x as CompositeFilterDescriptor;
			if (nested.filters) {
				this.normalizeCreatedDateFilter(nested);
				return;
			}

			const descriptor = x as FilterDescriptor;
			if (descriptor.field === 'createdDate' && descriptor.value && !(descriptor.value instanceof Date)) {
				descriptor.value = this.appService.parseDate(descriptor.value as any);
			}
		});
	}
}
